export function generateHeaderCSS(): string {
  return `
/* ============================================
   HEADER
   ============================================ */

.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: rgba(var(--c-white-rgb, 255 255 255) / 0.92);
  border-bottom: 1px solid var(--c-border);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  padding: var(--sp-2) 0;
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s ease;
  will-change: transform;
}

/* Mobile: Compact header */ /* QA: F-1 — Breakpoint 768→900 */
@media (max-width: 900px) {
  .header {
    height: 44px;
    padding: 0;
  }
  .header__inner {
    height: 44px;
  }
}

/* Partially Persistent Header States */
.header--hidden {
  transform: translateY(-100%);
}
.header--visible {
  transform: translateY(0);
}
.header--scrolled {
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

.dark .header {
  background: rgba(var(--c-black-rgb, 0 0 0) / 0.85);
  border-bottom-color: rgba(255,255,255,0.1);
}

.header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--sp-6);
}

.header__brand {
  display: flex;
  align-items: center;
  gap: var(--sp-3);
}

.header__logo {
  width: 28px;
  height: 28px;
  background: var(--c-primary);
  color: var(--c-on-primary, var(--c-white)); /* QA: A-3 */
  border-radius: var(--radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: var(--text-sm);
}

@media (min-width: 901px) { /* QA: F-1 */
  .header__logo {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-md);
  }
}
.header__logo img { width: 100%; height: 100%; object-fit: contain; }

.header__name {
  font-family: var(--font-display);
  font-weight: var(--weight-normal);
  font-size: var(--text-base);
  letter-spacing: var(--tracking-wide);
}

.header__nav {
  display: flex;
  align-items: center;
  gap: var(--sp-8);
}

.header__nav-link {
  font-size: var(--text-sm);
  color: var(--c-text-light);
  transition: color var(--duration-fast);
}
.header__nav-link:hover { color: var(--c-text); }

.header__cta {
  padding: var(--sp-2) var(--sp-4);
  font-size: var(--text-sm);
}

.header__dark-toggle {
  position: relative;
  width: 52px;
  height: 28px;
  padding: 0;
  background: #e2e8f0;
  border: 2px solid transparent;
  border-radius: 14px;
  cursor: pointer;
  transition: background 0.2s ease;
  margin: -8px;
  padding: 8px;
  box-sizing: content-box;
}
.header__dark-toggle::before {
  content: '';
  position: absolute;
  top: 10px;
  left: 10px;
  width: 22px;
  height: 22px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  transition: transform 0.25s cubic-bezier(0.4,0,0.2,1);
}
.header__dark-toggle::after {
  content: '';
  position: absolute;
  top: 14px;
  left: 14px;
  width: 14px;
  height: 14px;
  background: #f59e0b;
  border-radius: 50%;
  box-shadow: 0 0 0 2px #fbbf24;
  transition: transform 0.25s cubic-bezier(0.4,0,0.2,1);
}
.dark .header__dark-toggle { background: var(--c-primary); }
.dark .header__dark-toggle::before { transform: translateX(24px); }
.dark .header__dark-toggle::after {
  transform: translateX(24px);
  background: #fcd34d;
  box-shadow: inset 4px -2px 0 0 #fbbf24;
  width: 12px;
  height: 12px;
  top: 15px;
  left: 15px;
}
.header__dark-toggle:focus-visible {
  border-color: var(--c-primary);
  box-shadow: 0 0 0 3px rgba(var(--c-primary-rgb) / 0.3);
}

.header__toggle {
  display: none;
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1px solid var(--c-border);
  border-radius: var(--radius-md);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  cursor: pointer;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
}
.header__toggle:focus-visible {
  outline: 2px solid var(--c-primary);
  outline-offset: 2px;
}
.header__toggle span {
  display: block;
  width: 18px;
  height: 2px;
  background: var(--c-text);
  transition: all var(--duration-fast);
}
.header__toggle.open span:nth-child(1) { transform: rotate(45deg) translateY(5px); }
.header__toggle.open span:nth-child(2) { opacity: 0; }
.header__toggle.open span:nth-child(3) { transform: rotate(-45deg) translateY(-5px); }

.header__mobile-nav {
  display: none;
  position: fixed;
  inset: 0;
  background: var(--c-bg, #ffffff);
  z-index: 999;
  padding: calc(var(--sp-12) + var(--sp-8)) var(--sp-6) var(--sp-6);
  flex-direction: column;
  gap: var(--sp-4);
  overflow-y: auto;
}
.header__mobile-nav.open { display: flex; }
.header__mobile-link {
  display: block;
  padding: var(--sp-4);
  font-size: var(--text-xl);
  font-weight: 500;
  border-bottom: 1px solid var(--c-border);
}

@media (max-width: 900px) { /* QA: F-1 */
  .header__nav { display: none; }
  .header__toggle { display: flex; }
}
`;
}
